import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { datadogLogs } from '@datadog/browser-logs';
import { AdminElection, useLazyGetElectionsQuery } from 'services/adminCandidatesApi';

interface ElectionOption {
  value: string;
  label: string;
  election: AdminElection;
}

interface Props {
  inputId: string;
  onSelect: (election: AdminElection) => void;
  isDisabled?: boolean;
  excludeElectionIds?: string[];
}

export const ElectionSearchSelect: FC<Props> = ({
  inputId,
  onSelect,
  isDisabled = false,
  excludeElectionIds = [],
}) => {
  const [options, setOptions] = useState<ElectionOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [triggerGetElections] = useLazyGetElectionsQuery();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchElections = useCallback(
    async (search?: string) => {
      setIsLoading(true);
      try {
        const results = await triggerGetElections({ search }).unwrap();
        setOptions(
          results.map(e => ({
            value: e.election_id,
            label: e.display_info,
            election: e,
          }))
        );
      } catch (err) {
        datadogLogs.logger.error('Failed to load elections', {}, err as Error);
      } finally {
        setIsLoading(false);
      }
    },
    [triggerGetElections]
  );

  useEffect(() => {
    fetchElections();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchElections]);

  const handleInputChange = (inputValue: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchElections(inputValue || undefined);
    }, 400);
  };

  const visibleOptions = options.filter(
    option => !excludeElectionIds.includes(option.election.election_id)
  );

  return (
    <Select
      inputId={inputId}
      className="react-select"
      classNamePrefix="react-select"
      placeholder="Search elections..."
      isLoading={isLoading}
      isDisabled={isDisabled}
      options={visibleOptions}
      value={null}
      onInputChange={handleInputChange}
      onChange={(option: ElectionOption | null) => option && onSelect(option.election)}
      filterOption={() => true}
    />
  );
};
